import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSocietyAdminData,
  getAdminSocietyDetail,
  getBillCategories,
  createSocietyBills,
  AdminSocietyFlat,
  BillCategoryItem,
} from '../services/api';
import {
  uniqueFlatTypeDisplays,
  flatMatchesSelectedTypes,
  normalizeFlatTypeKey,
} from '../utils/flatTypeNormalize';
import './SocietyAdminCreateBill.css';

const SocietyAdminCreateBill: React.FC = () => {
  const navigate = useNavigate();
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [flats, setFlats] = useState<AdminSocietyFlat[]>([]);
  const [categories, setCategories] = useState<BillCategoryItem[]>([]);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [billType, setBillType] = useState('');
  const [amount, setAmount] = useState('');
  const [amountPerSqft, setAmountPerSqft] = useState(false);
  const [billDate, setBillDate] = useState(() => new Date().toISOString().split('T')[0]!);
  const [dueDays, setDueDays] = useState('');
  const [useCustomAmounts, setUseCustomAmounts] = useState(false);
  const [customAmounts, setCustomAmounts] = useState<Record<number, string>>({});

  // Flat selection
  const [exemptedFlatIds, setExemptedFlatIds] = useState<Set<number>>(new Set());
  const [flatSearch, setFlatSearch] = useState('');
  const [selectedFlatTypes, setSelectedFlatTypes] = useState<string[]>([]);

  useEffect(() => {
    if (!societyId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [detailRes, catRes] = await Promise.all([
          getAdminSocietyDetail(societyId),
          getBillCategories(societyId),
        ]);
        if (detailRes.success && detailRes.data?.flats) {
          setFlats(detailRes.data.flats);
        }
        if (catRes.success && catRes.data) {
          const active = catRes.data.filter((c) => c.is_active === 1);
          setCategories(active);
          if (active.length > 0 && !billType) {
            setBillType(active[0]!.bill_type_key);
          }
        }
      } catch (err: any) {
        setAlert({ type: 'error', message: err.message || 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [societyId]);

  const uniqueFlatTypes = useMemo(
    () => uniqueFlatTypeDisplays(flats.map((f) => f.flat_type)),
    [flats],
  );

  const filteredFlats = useMemo(() => {
    const q = flatSearch.trim().toLowerCase();
    return flats.filter((f) => {
      if (!flatMatchesSelectedTypes(f.flat_type, selectedFlatTypes)) return false;
      if (!q) return true;
      return (
        f.flat_number.toLowerCase().includes(q) ||
        (!!f.block_name && f.block_name.toLowerCase().includes(q)) ||
        (!!f.flat_type && f.flat_type.toLowerCase().includes(q))
      );
    });
  }, [flats, flatSearch, selectedFlatTypes]);

  const selectedFlats = useMemo(
    () => flats.filter((f) => !exemptedFlatIds.has(f.id)),
    [flats, exemptedFlatIds]
  );

  const toggleExemption = (flatId: number) => {
    setExemptedFlatIds((prev) => {
      const next = new Set(prev);
      if (next.has(flatId)) next.delete(flatId);
      else next.add(flatId);
      return next;
    });
  };

  const selectAll = () => {
    setExemptedFlatIds((prev) => {
      const next = new Set(prev);
      for (const f of filteredFlats) next.delete(f.id);
      return next;
    });
  };

  const deselectAll = () => {
    setExemptedFlatIds((prev) => {
      const next = new Set(prev);
      for (const f of filteredFlats) next.add(f.id);
      return next;
    });
  };

  const toggleFlatTypeFilter = (type: string) => {
    setSelectedFlatTypes((prev) => {
      const key = normalizeFlatTypeKey(type);
      const has = prev.some((t) => normalizeFlatTypeKey(t) === key);
      if (has) return prev.filter((t) => normalizeFlatTypeKey(t) !== key);
      return [...prev, type];
    });
  };

  const handleCustomAmountChange = (flatId: number, value: string) => {
    setCustomAmounts((prev) => ({ ...prev, [flatId]: value }));
  };

  const canSubmit =
    title.trim() &&
    description.trim() &&
    billType &&
    billDate &&
    selectedFlats.length > 0 &&
    (useCustomAmounts
      ? selectedFlats.every((f) => {
          const v = customAmounts[f.id];
          return v && parseFloat(v) > 0;
        })
      : parseFloat(amount) > 0);

  const handleSubmit = async () => {
    if (!societyId || !canSubmit) return;
    setSubmitting(true);
    setAlert(null);

    try {
      if (useCustomAmounts) {
        // Group flats by amount and create separate calls per unique amount
        const amountGroups = new Map<number, number[]>();
        for (const flat of selectedFlats) {
          const amt = parseFloat(customAmounts[flat.id] || '0');
          if (amt <= 0) continue;
          const existing = amountGroups.get(amt) || [];
          existing.push(flat.id);
          amountGroups.set(amt, existing);
        }

        let totalCreated = 0;
        const amountEntries = Array.from(amountGroups.entries());
        for (let i = 0; i < amountEntries.length; i++) {
          const amt = amountEntries[i][0];
          const flatIds = amountEntries[i][1];
          const res = await createSocietyBills({
            society_id: societyId,
            requestType: billType,
            bill_type: billType,
            title: title.trim(),
            description: description.trim(),
            flats: flatIds,
            amount: amt,
            billDate,
            ...(dueDays && parseInt(dueDays, 10) > 0 ? { due_days: parseInt(dueDays, 10) } : {}),
          });
          if (res.success) totalCreated += res.data.length;
        }
        setAlert({
          type: 'success',
          message: `${totalCreated} bill(s) created successfully!`,
        });
      } else {
        const res = await createSocietyBills({
          society_id: societyId,
          requestType: billType,
          bill_type: billType,
          title: title.trim(),
          description: description.trim(),
          flats: selectedFlats.map((f) => f.id),
          amount: parseFloat(amount),
          amount_per_sqft: amountPerSqft || undefined,
          billDate,
          ...(dueDays && parseInt(dueDays, 10) > 0 ? { due_days: parseInt(dueDays, 10) } : {}),
        });
        if (res.success) {
          setAlert({
            type: 'success',
            message: res.message || `${res.data.length} bill(s) created successfully!`,
          });
        }
      }

      // Reset form after success
      setTitle('');
      setDescription('');
      setAmount('');
      setDueDays('');
      setUseCustomAmounts(false);
      setCustomAmounts({});
      setExemptedFlatIds(new Set());
      setSelectedFlatTypes([]);
      setFlatSearch('');
      setAmountPerSqft(false);
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to create bills' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!societyId) {
    return <div className="cb-loading">No society selected.</div>;
  }

  if (loading) {
    return <div className="cb-loading">Loading...</div>;
  }

  const selectedCategory = categories.find((c) => c.bill_type_key === billType);

  return (
    <div className="cb-container">
      <div className="cb-header">
        <h2>Create Bill</h2>
        <p className="cb-subtitle">
          Generate bills for all flats in your society. You can exempt specific flats or set custom amounts.
        </p>
      </div>

      {alert && (
        <div className={`cb-alert cb-alert-${alert.type}`}>
          {alert.type === 'success' ? '✓' : '✕'} {alert.message}
        </div>
      )}

      {/* Bill Details */}
      <div className="cb-card">
        <h3 className="cb-card-title">Bill Details</h3>

        <div className="cb-form-row">
          <div className="cb-form-group">
            <label className="cb-label cb-label-required">Category</label>
            <select
              className="cb-select"
              value={billType}
              onChange={(e) => setBillType(e.target.value)}
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.bill_type_key} value={c.bill_type_key}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="cb-form-group">
            <label className="cb-label cb-label-required">Bill Date</label>
            <input
              type="date"
              className="cb-input"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
            />
          </div>
        </div>

        <div className="cb-form-group">
          <label className="cb-label cb-label-required">Title</label>
          <input
            type="text"
            className="cb-input"
            placeholder="e.g. Monthly Maintenance - June 2026"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="cb-form-group">
          <label className="cb-label cb-label-required">Description</label>
          <textarea
            className="cb-textarea"
            placeholder="Bill description / remarks"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        <div className="cb-form-row-3">
          <div className="cb-form-group">
            <label className="cb-label cb-label-required">
              {useCustomAmounts ? 'Default Amount (₹)' : 'Amount (₹)'}
            </label>
            <input
              type="number"
              className="cb-input"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              disabled={useCustomAmounts}
            />
            {!useCustomAmounts && (
              <span className="cb-help">Same amount for all selected flats</span>
            )}
          </div>
          <div className="cb-form-group">
            <label className="cb-label">Due Days</label>
            <input
              type="number"
              className="cb-input"
              placeholder="e.g. 15"
              value={dueDays}
              onChange={(e) => setDueDays(e.target.value)}
              min="0"
            />
            <span className="cb-help">Days after bill date (optional)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}>
            <label className="cb-toggle-label">
              <input
                type="checkbox"
                checked={amountPerSqft}
                onChange={(e) => setAmountPerSqft(e.target.checked)}
                disabled={useCustomAmounts}
              />
              Amount per sq.ft
            </label>
            <label className="cb-toggle-label">
              <input
                type="checkbox"
                checked={useCustomAmounts}
                onChange={(e) => {
                  setUseCustomAmounts(e.target.checked);
                  if (e.target.checked) setAmountPerSqft(false);
                }}
              />
              Custom amount per flat
            </label>
          </div>
        </div>
      </div>

      {/* Flat Selection */}
      <div className="cb-card">
        <h3 className="cb-card-title">Select Flats</h3>

        <div className="cb-flat-toolbar">
          <div className="cb-flat-toolbar-left">
            <span className="cb-flat-count">
              <strong>{selectedFlats.length}</strong> of {flats.length} flats selected
              {exemptedFlatIds.size > 0 && (
                <> &middot; <span style={{ color: '#dc2626' }}>{exemptedFlatIds.size} exempted</span></>
              )}
              {(selectedFlatTypes.length > 0 || flatSearch.trim()) && (
                <> &middot; showing {filteredFlats.length}</>
              )}
            </span>
            <button type="button" className="cb-btn-text" onClick={selectAll}>
              Select All
            </button>
            <button type="button" className="cb-btn-text" onClick={deselectAll}>
              Deselect All
            </button>
          </div>
          <input
            type="text"
            className="cb-flat-search"
            placeholder="Search flat / block..."
            value={flatSearch}
            onChange={(e) => setFlatSearch(e.target.value)}
          />
        </div>

        {uniqueFlatTypes.length > 0 && (
          <div className="cb-flat-type-filters">
            <span className="cb-flat-type-label">Flat type</span>
            {uniqueFlatTypes.map((type) => {
              const active = selectedFlatTypes.some(
                (t) => normalizeFlatTypeKey(t) === normalizeFlatTypeKey(type),
              );
              return (
                <button
                  key={type}
                  type="button"
                  className={`cb-flat-type-chip ${active ? 'active' : ''}`}
                  onClick={() => toggleFlatTypeFilter(type)}
                >
                  {type}
                </button>
              );
            })}
            {selectedFlatTypes.length > 0 && (
              <button
                type="button"
                className="cb-btn-text"
                onClick={() => setSelectedFlatTypes([])}
              >
                Clear types
              </button>
            )}
          </div>
        )}

        <div className="cb-flat-grid">
          {filteredFlats.map((flat) => {
            const isExempted = exemptedFlatIds.has(flat.id);
            return (
              <label
                key={flat.id}
                className={`cb-flat-chip ${isExempted ? 'exempted' : 'selected'}`}
              >
                <input
                  type="checkbox"
                  checked={!isExempted}
                  onChange={() => toggleExemption(flat.id)}
                />
                <div className="cb-flat-chip-info">
                  <span className="cb-flat-chip-number">{flat.flat_number}</span>
                  {flat.block_name && (
                    <span className="cb-flat-chip-block">{flat.block_name}</span>
                  )}
                  {flat.flat_type && (
                    <span className="cb-flat-chip-type">{flat.flat_type}</span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Custom Amounts */}
      {useCustomAmounts && selectedFlats.length > 0 && (
        <div className="cb-card">
          <h3 className="cb-card-title">Custom Amounts</h3>
          <table className="cb-custom-table">
            <thead>
              <tr>
                <th>Flat</th>
                <th>Block</th>
                {flats.some((f) => f.area) && <th>Area (sq.ft)</th>}
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {selectedFlats.map((flat) => (
                <tr key={flat.id}>
                  <td style={{ fontWeight: 600 }}>{flat.flat_number}</td>
                  <td>{flat.block_name || '—'}</td>
                  {flats.some((f) => f.area) && <td>{flat.area ?? '—'}</td>}
                  <td>
                    <input
                      type="number"
                      className="cb-custom-input"
                      placeholder="0.00"
                      value={customAmounts[flat.id] || ''}
                      onChange={(e) => handleCustomAmountChange(flat.id, e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      {selectedFlats.length > 0 && (title || billType) && (
        <div className="cb-summary">
          <h4 className="cb-summary-title">Bill Summary</h4>
          <div className="cb-summary-grid">
            <div className="cb-summary-item">
              <strong>Category:</strong> {selectedCategory?.name || billType || '—'}
            </div>
            <div className="cb-summary-item">
              <strong>Bill Date:</strong> {billDate || '—'}
            </div>
            <div className="cb-summary-item">
              <strong>Flats:</strong> {selectedFlats.length} flat(s)
            </div>
            <div className="cb-summary-item">
              <strong>Amount:</strong>{' '}
              {useCustomAmounts
                ? `Custom (₹${Object.values(customAmounts)
                    .reduce((s, v) => s + (parseFloat(v) || 0), 0)
                    .toLocaleString('en-IN')} total)`
                : amount
                  ? `₹${parseFloat(amount).toLocaleString('en-IN')}${amountPerSqft ? ' per sq.ft' : ' per flat'}`
                  : '—'}
            </div>
            {dueDays && parseInt(dueDays, 10) > 0 && (
              <div className="cb-summary-item">
                <strong>Due:</strong> {dueDays} days after bill date
              </div>
            )}
            {exemptedFlatIds.size > 0 && (
              <div className="cb-summary-item">
                <strong>Exempted:</strong> {exemptedFlatIds.size} flat(s)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="cb-actions">
        <button
          className="cb-btn-secondary"
          onClick={() => navigate('/society/overview')}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          className="cb-btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting
            ? 'Creating Bills...'
            : `Create ${selectedFlats.length} Bill${selectedFlats.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
};

export default SocietyAdminCreateBill;
